/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormButton } from '@/components/Form'
import { AddToCartSuccess } from '@/components/Icon/AddToCartSuccess'
import { CompareClose } from '@/components/Icon/CompareClose'
import { PopupClose } from '@/components/Icon/PopupClose'
import ImageOptimize from '@/components/ImageOptimize'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import PopupBody from '@/components/Popup/PopupBody'
import PopupFrame from '@/components/Popup/PopupFrame'
import PopupTitle from '@/components/Popup/PopupTitle'
import Price from '@/components/Price'
import ProductButton from '@/components/ProductDetail/ButtonProduct'
import { AddToCartType, ProductCardType } from '@/constants'
import { useCart, useStore } from '@/containers'
import { Meta } from '@/layouts/Meta'
import { StarRated } from '@/styles/Icon'
import { Main } from '@/templates/Main'
import { type Product, type Property, type PropertyValue } from '@/types'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getMenus,
  getProductWithPaginate,
  getQuickMenu,
  getSaleRegion,
} from '@/utils/endpoint'
import clsx from 'clsx'
import { getCookies } from 'cookies-next'
import { keyBy, uniqBy } from 'lodash'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

const renderSpecificationsItem = (item?: Property): ReactNode => {
  if (!item) {
    return null
  }
  switch (item?.type as string) {
    case 'multipleSelect':
      return item?.values?.map((e: PropertyValue, i: number) => {
        return (
          <span key={i}>
            {getTranslate(e?.value)}
            {i < item?.values?.length - 1 && ', '}
          </span>
        )
      })
    case 'singleSelect':
      return item?.values?.map((e: PropertyValue, i: number) => {
        return e?.link ? (
          <Link key={i} href={e?.link || ''} target='_blank'>
            <span className="mr-1 inline-block cursor-pointer text-primary-base after:content-[','] last:mr-0 last:after:content-[unset]">
              {getTranslate(e?.value)}
            </span>
          </Link>
        ) : (
          <span
            className="mr-1 inline-block after:content-[','] last:mr-0 last:after:content-[unset]"
            key={i}
          >
            {getTranslate(e?.value)}
          </span>
        )
      })
    case 'input':
      return (
        <div
          className='whitespace-pre-line'
          dangerouslySetInnerHTML={{
            __html: getTranslate(item?.value),
          }}
        ></div>
      )
    default:
      break
  }
}

const Compare = () => {
  const router = useRouter()
  const breadcrumbs = [{ label: 'So sánh', href: '/compare' }]
  const { setProductCompares, productCompares, setIsOpenAddComparePopup } =
    useStore()
  const { addToCart, cartLoading } = useCart()
  const [, setLoading] = useState(false)
  const [products, setProducts] = useState<{
    [key: string]: Product
  }>({})
  const [properties, setProperties] = useState<Property[]>([])
  const [isOpenAddToCart, setIsOpenAddToCart] = useState(false)

  const productCompareActive = useMemo(() => {
    return productCompares.filter(e => e?._id)
  }, [productCompares])

  useEffect(() => {
    if (productCompares.filter(e => e?._id).length > 0) {
      getProductWithPaginate({
        limit: 3,
        sortBy: 'score',
        detailProperty: true,
        displayProperty: true,
        sortType: 'desc',
        ids: productCompares.map(e => e?._id).join(','),
      }).then(res => {
        setProducts(keyBy(res?.data, '_id'))
        setLoading(false)
        setProperties(
          uniqBy(
            res?.data.map(e => e?.properties?.filter(f => !f.policy)).flat(),
            '_id'
          )
        )
      })
    }
  }, [productCompares])

  const handleAddToCompare = (e: any) => {
    if (!e?._id) {
      return setIsOpenAddComparePopup(true)
    }
    return router.push(
      `/${getTranslate(products[e?._id || '']?.slugs.value)}${
        products[e?._id || '']?.slugs.postfix
      }`
    )
  }

  return (
    <Main meta={<Meta title={'Khuyến mãi'} description={'Khuyễn mãi'} />}>
      <section className='promotionPage min-h-[500px] pb-10'>
        <PageBreadcrumb
          homeText='Trang chủ'
          breadcrumbs={breadcrumbs}
          className='mb-[20px]'
        />
        <div className='comparePopupContent h-full'>
          <div className='border-dark-1500 grid h-full grid-cols-4 gap-x-[20px] rounded border'>
            <div className='ml-[15px] mt-[15px] flex h-full flex-col'>
              <h3 className='mb-[15px] text-fieldLabel'>So sánh sản phẩm</h3>
              {productCompares &&
                productCompares.map((e, i) => (
                  <div key={e?._id}>
                    <p className='text-fieldLabel font-bold'>{e?.name || ''}</p>
                    {i < productCompareActive?.length && (
                      <span className='py-[8px]'>&</span>
                    )}
                  </div>
                ))}
            </div>
            {productCompares.length > 0 &&
              productCompares.map((e, index) => (
                <div
                  key={`productCompare_${index}`}
                  className='relative flex flex-col items-center justify-center border-l px-[40px] pb-[30px] pt-[15px] text-center'
                >
                  {e?._id && (
                    <span
                      onClick={() => {
                        return setProductCompares([
                          ...productCompares
                            .map(f => (f._id === e?._id ? {} : f))
                            .sort((a, b) => {
                              if (
                                Object.keys(a).length === 0 &&
                                Object.keys(b).length === 0
                              ) {
                                return 0
                              }
                              if (Object.keys(a).length === 0) return 1
                              if (Object.keys(b).length === 0) return -1
                              return 0
                            }),
                        ])
                      }}
                      className='absolute right-[15px] top-[10px] cursor-pointer'
                    >
                      <CompareClose></CompareClose>
                    </span>
                  )}

                  <ImageOptimize
                    onClick={() => handleAddToCompare(e)}
                    className={`${!e?._id && 'mb-[10px] cursor-pointer'}`}
                    width={!e?._id ? 60 : 148}
                    height={!e?._id ? 60 : 148}
                    quality={100}
                    src={
                      products[e?._id || '']?.images[0] ||
                      '/../../assets/images/compare-default.svg'
                    }
                    alt={products[e?._id || '']?.name?.vi || ''}
                    priority={true}
                  />
                  <div
                    onClick={() => handleAddToCompare(e)}
                    className='mb-2 line-clamp-2 block h-[40px] cursor-pointer overflow-hidden text-ellipsis text-left text-base capitalize leading-5 text-dark-400 focus:border-0 focus:outline-0'
                  >
                    {products[e?._id || '']?.name?.vi || 'Thêm sản phẩm'}
                  </div>
                  <Price
                    type={ProductCardType?.PRODUCT_CARD}
                    originPrice={products[e?._id || '']?.price}
                    finalPrice={products[e?._id || '']?.finalPrice}
                    link={
                      '/' +
                      getTranslate(products[e?._id || '']?.slugs.value) +
                      products[e?._id || '']?.slugs?.postfix
                    }
                  />
                </div>
              ))}
          </div>

          <div className='compareProperty'>
            <h3 className='mb-[20px] mt-[18px] text-center text-titleSection font-bold'>
              Hình ảnh & thông số kỹ thuật
            </h3>

            <div className='mb-[20px] grid grid-cols-4'>
              <div className='border-dark-1500 flex h-full flex-col rounded border'>
                <h3 className='mb-[15px] ml-[17px] mt-2 text-fieldLabel font-bold'>
                  Đặc điểm nổi bật
                </h3>
              </div>
              {productCompareActive?.map((p, index) => (
                <div
                  key={`specific${index}`}
                  className='border-dark-1500 border py-[15px] pl-[20px] pr-[15px]'
                >
                  {products[p._id || '']?.outstandingFeatures?.map(
                    (item, index) => (
                      <div className='flex ' key={index}>
                        <span className='mt-2 flex h-[5px] max-w-[5px] flex-[5px] items-center justify-center rounded-full  border border-dark-200 bg-black-400 text-sm'></span>
                        <span className='ml-3 flex-2 leading-6 text-dark-400'>
                          {getTranslate(item?.value)}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>

            {properties.map(e => (
              <div key={e?._id} className='mb-1 grid grid-cols-4'>
                <div className='border-dark-1500 flex h-full flex-col border'>
                  <h3 className='mb-[15px] ml-[17px] mt-2 text-fieldLabel font-bold'>
                    {getTranslate(e?.name)}
                  </h3>
                </div>
                {productCompareActive?.map((p, index) => (
                  <div
                    className='border-dark-1500 border py-[15px] pl-[20px] pr-[15px]'
                    key={`productC${index}`}
                  >
                    {renderSpecificationsItem(
                      products[p._id || '']?.properties.find(
                        r => r._id === e?._id
                      )
                    )}
                  </div>
                ))}
              </div>
            ))}

            <div className='mb-[20px] grid grid-cols-4'>
              <div className='border-dark-1500 flex h-full flex-col rounded border'>
                <h3 className='mb-[15px] ml-[17px] mt-2 text-fieldLabel font-bold'>
                  Đánh giá & review
                </h3>
              </div>
              {productCompareActive?.map((p, index) => (
                <div
                  key={`review_${index}`}
                  className='border-dark-1500 flex flex-row border py-[15px] pl-[20px] pr-[15px]'
                >
                  <div className='text-black mb-1.5 flex gap-1 text-center tracking-tight'>
                    <span className='current'>
                      {products[p._id || '']?.average
                        ? products[p._id || ''].average
                        : '5'}
                    </span>
                    <StarRated className='relative top-[3px]' />
                  </div>
                  <div className='border-dark-1500 ml-[10px] h-[25px] border-l-[2px] pl-[15px]'>
                    0 đánh giá
                  </div>
                </div>
              ))}
            </div>

            {/* add to cart */}
            <div className='mb-[20px] grid grid-cols-4'>
              <div className='border-dark-1500 flex h-full flex-col rounded border'></div>
              {productCompareActive?.length > 0 &&
                productCompareActive?.map((r, index) => (
                  <div
                    key={`productButton${index}`}
                    className='border-dark-1500 border py-[15px] pl-[20px] pr-[15px]'
                  >
                    <ProductButton
                      className='mb-[10px]'
                      type={AddToCartType?.BUY_NOW}
                      onClick={() =>
                        addToCart(products[r._id || ''], AddToCartType.BUY_NOW)
                      }
                      isProcessing={cartLoading}
                    />
                    <ProductButton
                      type={AddToCartType.ADD}
                      onClick={() => {
                        setIsOpenAddToCart(true)
                        addToCart(products[r._id || ''], AddToCartType.ADD)
                      }}
                      isProcessing={cartLoading}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>
      {/* Popup add to cart */}
      <PopupFrame
        open={isOpenAddToCart}
        onClose={() => {
          setIsOpenAddToCart(false)
        }}
        className='min-h-[361px] w-[480px] max-w-[520px] bg-white'
      >
        <PopupTitle className='h-[48px] border-b border-dark-200'>
          {
            <div
              onClick={() => setIsOpenAddToCart(false)}
              className='absolute right-[12px] top-[40%] flex translate-y-[-40%] cursor-pointer items-center gap-1'
            >
              <PopupClose className='text-dark-400' />
              Đóng
            </div>
          }
        </PopupTitle>
        <PopupBody>
          <div className='listLocation mt-[18px] flex h-full max-h-[300px] flex-col items-center justify-center gap-y-[12px] overflow-scroll border-dark-100 px-[24px] scrollbar-hide'>
            <AddToCartSuccess />
            <h3 className='text-HeadlineMedium mt-[26px] font-bold text-dark-400'>
              Thông báo
            </h3>
            <p className='mt-[8px] text-Headline text-dark-400'>
              Sản phẩm của bạn đã được thêm vào giỏ hàng
            </p>
            <FormButton
              onClick={() => {
                setIsOpenAddToCart(false)
                router.push('/checkout/cart')
              }}
              className={clsx(
                'disabled:text-[rgb(0 0 0 / 25%)] mt-[32px] h-[40px] w-[200px] rounded border bg-primary-second text-fieldInput font-bold text-white disabled:outline-none'
              )}
            >
              Xem giỏ hàng
            </FormButton>
          </div>
        </PopupBody>
      </PopupFrame>
    </Main>
  )
}

export default Compare

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const [regions, menus, banner, quickMenu, bottomMenu] = await Promise.all([
    getSaleRegion(),
    getMenus(),
    getBannerSSR(),
    getQuickMenu(),
    getBottomMenu(),
  ])

  return {
    props: {
      regions,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      bottomMenu,
      token: token || null,
    },
  }
}
